import React, { useEffect, useRef, useState } from "react";
import { isNative } from "../../utils/platform";
import { haptic } from "../../utils/nativeBridge";

/**
 * PullToRefresh — drop a wrapper around any scrollable area to add an
 * iOS-style pull-to-refresh gesture. Native only by default; the web
 * bundle skips the gesture entirely (rendering just `children`) since
 * web users have no equivalent affordance and a refresh button works
 * better there.
 *
 * Usage:
 *   <PullToRefresh onRefresh={async () => { await refetch(); }}>
 *     <div ref={scrollRef} style={{flex:1, overflow:"auto"}}>...</div>
 *   </PullToRefresh>
 *
 * The inner element MUST be the scroll container — we listen for touch
 * events on it and only trigger the gesture when scrollTop is 0.
 *
 * threshold (px): how far down the user must pull before release fires
 *   onRefresh. Defaults to 70.
 * maxPull (px): cap on the indicator translation so the user can't drag
 *   the whole page off-screen. Defaults to 110.
 */
export function PullToRefresh({ children, onRefresh, threshold = 70, maxPull = 110, disabled = false }) {
  const wrapRef = useRef(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startRef = useRef({ y: 0, active: false, primed: false });
  const armedRef = useRef(false);

  // We only attach the gesture on native. Falling back to plain children
  // on web keeps DOM identical for SSR / hydration.
  const enabled = isNative() && !disabled;

  useEffect(() => {
    if (!enabled) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    // The scroll container is the first child element rendered by the
    // caller. We listen on it directly so we can read scrollTop.
    const scroller = wrap.firstElementChild;
    if (!scroller) return;

    const onTouchStart = (e) => {
      if (refreshing) return;
      // Only prime the gesture when at the very top of the scroller.
      if (scroller.scrollTop > 0) { startRef.current.active = false; return; }
      startRef.current.y = e.touches[0]?.clientY || 0;
      startRef.current.active = true;
      startRef.current.primed = false;
    };

    const onTouchMove = (e) => {
      if (!startRef.current.active || refreshing) return;
      const dy = (e.touches[0]?.clientY || 0) - startRef.current.y;
      if (dy <= 0) { setPull(0); return; }
      // Resistive curve so the pull feels like rubber the further you go.
      const eased = Math.min(maxPull, Math.pow(dy, 0.85));
      setPull(eased);
      // Once the gesture is clearly a pull (not a tap), prevent the
      // browser from interpreting it as a normal scroll.
      if (eased > 4 && e.cancelable) { try { e.preventDefault(); } catch {} }
      // Arm haptic when crossing the threshold.
      if (!armedRef.current && eased >= threshold) {
        armedRef.current = true;
        haptic("selection");
      }
      if (armedRef.current && eased < threshold) {
        armedRef.current = false;
      }
    };

    const onTouchEnd = async () => {
      if (!startRef.current.active) return;
      startRef.current.active = false;
      const fired = pull >= threshold && !refreshing;
      armedRef.current = false;
      if (fired) {
        setRefreshing(true);
        setPull(56); // hold the indicator while refreshing
        try { await onRefresh?.(); } catch {}
        await haptic("success");
        setRefreshing(false);
        setPull(0);
      } else {
        setPull(0);
      }
    };

    scroller.addEventListener("touchstart", onTouchStart, { passive: true });
    scroller.addEventListener("touchmove", onTouchMove, { passive: false });
    scroller.addEventListener("touchend", onTouchEnd, { passive: true });
    scroller.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      scroller.removeEventListener("touchstart", onTouchStart);
      scroller.removeEventListener("touchmove", onTouchMove);
      scroller.removeEventListener("touchend", onTouchEnd);
      scroller.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, onRefresh, threshold, maxPull, pull, refreshing]);

  if (!enabled) return <>{children}</>;

  // Translate the children downward by the pull amount, render an
  // indicator that fades in and rotates as the user crosses threshold.
  const progress = Math.min(1, pull / threshold);
  const visible = pull > 4 || refreshing;

  return (
    <div ref={wrapRef} className="bam-ptr-host" style={{position:"relative",height:"100%",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div aria-hidden="true" style={{
        position:"absolute", top: 0, left: 0, right: 0,
        height: 56,
        display:"flex", alignItems:"center", justifyContent:"center",
        opacity: visible ? 1 : 0,
        transform: `translateY(${Math.min(pull, maxPull) - 56}px)`,
        transition: refreshing ? "none" : "opacity .12s",
        pointerEvents: "none",
        zIndex: 1,
      }}>
        <div style={{
          width:24, height:24, borderRadius:"50%",
          border:"2px solid rgba(226,221,159,0.25)",
          borderTopColor: "#E2DD9F",
          animation: refreshing ? "bamPtrSpin .8s linear infinite" : undefined,
          transform: refreshing ? undefined : `rotate(${progress * 360}deg)`,
          transition: refreshing ? undefined : "transform .05s linear",
        }}/>
      </div>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        const existing = child.props.style || {};
        return React.cloneElement(child, {
          style: {
            ...existing,
            transform: `translate3d(0, ${Math.min(pull, maxPull)}px, 0)`,
            transition: startRef.current.active || refreshing ? "none" : "transform .18s cubic-bezier(.2,.8,.2,1)",
            willChange: "transform",
          },
        });
      })}
    </div>
  );
}

export default PullToRefresh;
